import classNames from 'classnames';
import { useSetAtom } from 'jotai';
import Quill, { Delta } from 'quill/core';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router';
import { useEffectEvent } from 'use-effect-event';
import { useQuery, useSubscription } from '@apollo/client';
import Page from '../app/layout/Page';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import {
  ACTION_EDITOR_RENDER_DURATION,
  ACTION_TYPING_APDEX,
  CATEGORY_PRODUCT,
} from '../constants/analytics';
import { GRANT_ACCESS_TO } from '../constants/params';
import useTestHelpersInDev from '../dev/useTestHelpersInDev';
import { AssetTokenProvider } from '../editor/AssetTokenContext';
import Editor from '../editor/Editor';
import type {
  PerformanceMeasureHandler,
  TextChangeHandler,
} from '../editor/Editor';
import QuillContext, { useQuill } from '../editor/QuillContext';
import UIElementResizeContext from '../editor/UIElementResizeContext';
import WiderLineSizeContext from '../editor/WiderLineSizeContext';
import { MentionPickerProvider } from '../editor/controls/MentionPicker';
import { EditorActions } from '../editor/editorSlice';
import useAnchorScrollingHandler, {
  useScrollingToAnchorOnLoad,
} from '../editor/useAnchorScrollingHandler';
import { gql } from '../graphql/generated/gql';
import type { DocumentType } from '../graphql/generated/gql';
import { getSearchContext } from '../search/searchContext';
import { postToasts } from '../toasts/toasts';
import InternalTopbar from '../topbar/InternalTopbar';
import { useSavePostToRecentlyViewed } from '../topbar/SearchBar';
import { UIActions } from '../ui/uiSlice';
import { useAllUsersMap, useGetUser } from '../user/useAllUsers';
import Logger from '../utils/Logger';
import PageMeta from '../utils/PageMeta';
import { track } from '../utils/analytics';
import { isElectron } from '../utils/desktopClient';
import useErrorRedirectEffect from '../utils/error/useErrorRedirectEffect';
import useMainContentHistory from '../utils/router/useMainContentHistory';
import useNavigate from '../utils/router/useNavigate';
import useRemoveHash from '../utils/router/useRemoveHash';
import useSlug from '../utils/router/useSlug';
import { getThreadIdFromUrlHash } from '../utils/url/commentThreadHash';
import postPath from '../utils/url/postPath';
import CreateVerificationModal from './CreateVerificationModal';
import HoverContentPreview from './HoverContentPreview';
import MagneticWall from './MagneticWall';
import PendingMagneticWallItemProvider from './PendingMagneticWallItemProvider';
import PostActivityTracker from './PostActivityTracker';
import PostLayout from './PostLayout';
import PostLayoutProvider from './PostLayoutProvider';
import PostSaveTracker from './PostSaveTracker';
import PostSkeleton from './PostSkeleton';
import PostStickyControl from './PostStickyControl';
import * as styles from './PostView.module.scss';
import PostViewBannerEditor from './PostViewBannerEditor';
import VerifyPostModal from './VerifyPostModal';
import ControlBar, { FooterControlBar } from './controlBar/ControlBar';
import getVerificationStatus, {
  VerificationStatus,
} from './getVerificationStatus';
import ComparePostsView from './history/ComparePostsView';
import PostHistoryView from './history/PostHistoryView';
import isVerifier from './isVerifier';
import { PostPolicyAction, usePostPolicyCheck } from './postPolicyCheck';
import PostActions from './postView/PostActions';
import { isShareDropdownOpenAtom } from './postView/isShareDropdownOpenAtom';
import PostSeriesFooter from './series/PostSeriesFooter';
import { useAddPostUser } from './sharing/postPermissionOperations';
import PostStateBar from './stateBar/PostStateBar';
import PostTopicBar from './topic/PostTopicBar';
import useCachedPostTitle from './useCachedPostTitle';
import useCanDisplayPostHistoryView from './useCanDisplayPostHistoryView';
import { useIsPostDeletedLocally } from './useDeletePost';
import { useHasHeadings } from './useHasHeadings';
import useHasMagneticWallItems from './useHasMagneticWallItems';
import useInactivityObserver from './useInactivityObserver';
import useLazyHistoricalCommentThreadPath from './useLazyHistoricalCommentThreadPath';
import usePostTopicIdSetSubscription from './usePostTopicIdSetSubscription';
import useRequestPostAccess from './useRequestPostAccess';
import useShareSecret from './useShareSecret';
import useSyncShareSecretWithUrl from './useSyncShareSecretWithUrl';
import collectThreadAndAnnotationIds from './utils/collectThreadAndAnnotationIds';
import useIsLayoutSize from './utils/useIsLayoutSize';
import { LayoutSize } from './utils/useLayoutSize';
import useShouldShowEditorPlaceholder from './utils/useShouldShowEditorPlaceholder';

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const POST_QUERY = gql(/** @complexity 35 */ `
  query PostView(
    $id: ID!
    $queryId: String
    $resultId: String
    $position: Int
    $shareSecret: String
  ) {
    post(
      id: $id
      view: true
      queryId: $queryId
      resultId: $resultId
      position: $position
      shareSecret: $shareSecret
    ) {
      id
      externalPost {
        id
      }
      banner {
        original
      }
      verificationEntries {
        id
        verifier {
          id
          ...useGetUser_user
        }
      }
      series {
        id
        ...PostTopicBar_series
        ...FooterControlBar_series
        ...PostSeriesFooter_series
      }
      ...PostTopicBar_post
      ...Editor_post
      ...usePostPolicyCheck_post
      ...MagneticWall_post
      ...useSavePostToRecentlyViewed_post
      ...PostViewBannerEditor_post
      ...EditorActions_post
      ...getVerificationStatus_post
      ...isVerifier_post
      ...ControlBar_post
      ...useHasMagneticWallItems_post
      ...useSyncShareSecretWithUrl_post
      ...FooterControlBar_postDetails
      ...PostActions_post
      ...PostStateBar_post
      ...MentionPickerProvider_post
      ...CreateVerificationModal_post
      ...VerifyPostModal_post
    }
    entityAssetToken(entityId: $id, entityType: POST, shareSecret: $shareSecret) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

const POST_UPDATED_SUBSCRIPTION = gql(/** @complexity 4 */ `
  subscription postUpdate($postId: ID!) {
    postUpdated(postId: $postId) {
      id
      publishedAt
      archivedAt
    }
  }
`);

const POST_DELETED_SUBSCRIPTION = gql(/** @complexity 4 */ `
  subscription postDeleted($postId: ID!) {
    post: postDeleted(postId: $postId) {
      id
    }
  }
`);

const MAX_TRACKING_EVENT_COUNT = 20;
const useTypingPerformanceObserver = (postId: string) => {
  const { quill } = useQuill();
  const [shouldObserve, setShouldObserve] = useState(true);

  useEffect(() => {
    if (!quill || !shouldObserve) return undefined;

    const isEventTimingAPISupported =
      'PerformanceObserver' in window &&
      typeof PerformanceObserver === 'function' &&
      PerformanceObserver.supportedEntryTypes?.includes('event');

    if (!isEventTimingAPISupported) return undefined;

    // We use timestamp for event identifier to link a PerformanceObserver entry to a keydown event
    // https://github.com/w3c/event-timing/issues/108
    const eventTimeStamps = new Set<number>();
    const slowDurations: number[] = [];
    const alphanumericRegex = /^[a-zA-Z0-9]$/;

    let lastObserverEntryWaitingTimer: ReturnType<typeof setTimeout> | null =
      null;

    const handleKeydown = (e: KeyboardEvent) => {
      if (lastObserverEntryWaitingTimer) return;

      // Don't track shortcuts or Enter/Backspace keys because their performance varies a lot and
      // can make the Apdex less accurate.
      const shouldTrackPerformance =
        !e.isComposing &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.repeat &&
        alphanumericRegex.test(e.key);

      if (!shouldTrackPerformance) {
        return;
      }

      if (eventTimeStamps.size < MAX_TRACKING_EVENT_COUNT) {
        eventTimeStamps.add(e.timeStamp);
        return;
      }

      // Wait for PerformanceObserver to collect data for the last keydown event
      lastObserverEntryWaitingTimer = setTimeout(() => {
        setShouldObserve(false); // We already have enough data

        const frustratedDuration = 400;
        const frustratedCount = slowDurations.filter(
          (d) => d >= frustratedDuration,
        ).length;
        const toleratingCount = slowDurations.length - frustratedCount;
        const satisfiedCount = MAX_TRACKING_EVENT_COUNT - slowDurations.length;
        const apdex =
          (satisfiedCount + toleratingCount / 2) / MAX_TRACKING_EVENT_COUNT;

        track(ACTION_TYPING_APDEX, {
          category: CATEGORY_PRODUCT,
          label: postId,
          value: apdex,
        });

        if (apdex < 0.5) {
          Logger.warn('Slow typing warning', { postId, apdex });
        }
      }, 2000);
    };
    quill.root.addEventListener('keydown', handleKeydown);

    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.name === 'keydown' && eventTimeStamps.has(entry.startTime)) {
          slowDurations.push(entry.duration);
        }
      });
    });

    observer.observe({ type: 'event' });
    return () => {
      quill.root.removeEventListener('keydown', handleKeydown);
      observer.disconnect();
      if (lastObserverEntryWaitingTimer) {
        clearTimeout(lastObserverEntryWaitingTimer);
      }
    };
  }, [quill, shouldObserve, postId]);
};

const usePostDeleteSubscription = (postId: string) => {
  const dispatch = useAppDispatch();
  const isPostDeletedLocally = useIsPostDeletedLocally(postId);
  const mainContentHistory = useMainContentHistory();
  const handleSubscriptionData = () => {
    if (isPostDeletedLocally) return;

    dispatch(UIActions.pushToast(postToasts.postDeletedError));

    setTimeout(() => {
      mainContentHistory.back((route) => !route.pathname.includes(postId), {
        replace: true,
      });
    }, 0);
  };

  useSubscription(POST_DELETED_SUBSCRIPTION, {
    variables: { postId },
    onData: handleSubscriptionData,
  });
};

function useCommentHistoryRedirect(
  post?: DocumentType<typeof POST_QUERY>['post'],
) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const removeHash = useRemoveHash();

  const canDisplayPostHistoryView = useCanDisplayPostHistoryView();
  const getHistoricalCommentThreadPath = useLazyHistoricalCommentThreadPath();

  const content = post?.content.delta;

  useEffect(() => {
    const threadId = getThreadIdFromUrlHash(document.location.hash);
    const isThreadMissingInDelta =
      content &&
      threadId &&
      !collectThreadAndAnnotationIds(content).allThreadIds.has(threadId);

    let isMounted = true;

    if (
      post?.id &&
      threadId &&
      isThreadMissingInDelta &&
      canDisplayPostHistoryView
    ) {
      getHistoricalCommentThreadPath(post.id, threadId).then((redirectPath) => {
        if (!isMounted) {
          return;
        }
        if (redirectPath) {
          navigate(redirectPath, { replace: true });
        } else {
          dispatch(UIActions.pushToast(postToasts.threadNotFoundError));
          removeHash();
        }
      });
    }

    return () => {
      isMounted = false;
    };
  }, [
    post?.id,
    content,
    getHistoricalCommentThreadPath,
    navigate,
    dispatch,
    removeHash,
    canDisplayPostHistoryView,
  ]);
}

interface PostViewProps {
  postId: string;
}

const PostView = ({ postId }: PostViewProps) => {
  const dispatch = useAppDispatch();
  const shareSecret = useShareSecret();
  const originalShareSecretRef = useRef(shareSecret);

  useEffect(
    () => () => {
      dispatch(EditorActions.leave());
    },
    [dispatch, postId],
  );

  const { quill } = useQuill();
  useTestHelpersInDev();
  useTypingPerformanceObserver(postId);

  const readOnly = useAppSelector((state) => state.editor.readOnly);
  const isVersionLoaded = useAppSelector((state) => state.editor.version > -1);

  const cachedTitle = useCachedPostTitle(postId);
  const title = useAppSelector((state) => state.editor.title) || cachedTitle;

  const isLocked = useAppSelector(
    (state) => state.session.organization.isLocked,
  );
  const currentUser = useAppSelector((state) => state.session.user);
  const channel = useAppSelector((state) => state.editor.channel);
  useInactivityObserver(channel);

  const { queryId, resultId, position, highlightedRangeIndex } = useMemo(
    () => getSearchContext(postId),
    [postId],
  );

  const postLayoutRootRef = useRef<HTMLElement | null>(null);
  const innerBannerRef = useRef<HTMLDivElement | null>(null);

  // Remote updates not applied yet
  const receivedRef = useRef(new Delta());

  const { data, error, loading } = useQuery(POST_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      id: postId,
      queryId,
      resultId,
      position,
      shareSecret: originalShareSecretRef.current,
    },
  });

  const post = data?.post;

  useSyncShareSecretWithUrl(post);
  useCommentHistoryRedirect(post);
  usePostDeleteSubscription(postId);
  usePostTopicIdSetSubscription(postId);
  useSubscription(POST_UPDATED_SUBSCRIPTION, { variables: { postId } });

  const { requestAccess } = useRequestPostAccess({ postId });

  const isPostDeletedLocally = useIsPostDeletedLocally(postId);
  useErrorRedirectEffect({
    error,
    noun: 'post',
    forbiddenErrorToast: postToasts.noPostPermissionError({
      onRequestAccess: async () => {
        await requestAccess();
        setTimeout(() => {
          dispatch(UIActions.pushToast(postToasts.accessRequested));
        }, 250); // Wait until the first (wider) toast is dismissed to not change the layout
      },
    }),
    skip: isPostDeletedLocally,
  });

  useSavePostToRecentlyViewed(post);

  useScrollingToAnchorOnLoad(quill);

  const quillRef = useRef(quill);
  useEffect(() => {
    quillRef.current = quill;
    return () => {
      quillRef.current = null;
    };
  }, [quill]);

  const postPolicyCheck = usePostPolicyCheck();

  const isPostEditable =
    !!post &&
    !isLocked &&
    postPolicyCheck(PostPolicyAction.EditPost, post, currentUser) &&
    post.externalPost == null;

  const loadedPostIdRef = useRef<string | null>(null);
  const loadedIsPostEditableRef = useRef<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const userMap = useAllUsersMap();
  const getUser = useGetUser();
  const setIsShareDropdownOpen = useSetAtom(isShareDropdownOpenAtom);
  const addUser = useAddPostUser();

  useEffect(() => {
    if (!isElectron) return undefined;

    const cleanups: unknown[] = [
      window.desktop?.onFeatureRequested?.('sharePost', () => {
        setIsShareDropdownOpen({ open: true, autoFocus: true });
      }),
      window.desktop?.onFeatureRequested?.('print', () => {
        window.print();
      }),
    ];

    return () => {
      cleanups.forEach((cleanup) => typeof cleanup === 'function' && cleanup());
    };
  }, [setIsShareDropdownOpen]);

  const verificationStatus = post ? getVerificationStatus(post) : null;

  useEffect(() => {
    if (post && location.state?.verification) {
      if (verificationStatus === VerificationStatus.None) {
        dispatch(UIActions.openModal(CreateVerificationModal, { post }));
      } else if (
        isVerifier(post, currentUser) &&
        (verificationStatus === VerificationStatus.Verified ||
          verificationStatus === VerificationStatus.Expired)
      ) {
        dispatch(UIActions.openModal(VerifyPostModal, { post }));
      }
    }
  }, [location.state, dispatch, post, verificationStatus, currentUser]);

  const isCurrentUserVerifier = post ? isVerifier(post, currentUser) : false;

  const verifier = post?.verificationEntries[0]?.verifier
    ? getUser(post.verificationEntries[0].verifier)
    : null;

  useEffect(() => {
    const { verification, ...locationState } = location.state ?? {};
    if (verification && verificationStatus) {
      if (
        verifier &&
        verifier.id !== currentUser.id &&
        isCurrentUserVerifier &&
        verificationStatus === VerificationStatus.Verified
      ) {
        dispatch(
          UIActions.pushToast(
            postToasts.alreadyVerified({ verifierName: verifier.name }),
          ),
        );
      }
      navigate(location, { replace: true, state: locationState });
    }
  }, [
    currentUser.id,
    dispatch,
    isCurrentUserVerifier,
    location,
    navigate,
    verifier,
    verificationStatus,
    getUser,
  ]);

  const isMobile = useIsLayoutSize(LayoutSize.mobile);
  const getIsMobile = useEffectEvent(() => isMobile);

  useEffect(() => {
    if (
      post &&
      !error &&
      !loading &&
      (loadedPostIdRef.current !== post.id ||
        loadedIsPostEditableRef.current !== isPostEditable)
    ) {
      dispatch(
        EditorActions.load({
          post,
          isEditable: isPostEditable,
          isPublicView: false,
          isDefaultReadOnly: getIsMobile(),
          onRemoteDelta: (delta: Delta) => {
            receivedRef.current = receivedRef.current.compose(delta);
            quillRef.current?.updateContents(
              receivedRef.current,
              Quill.sources.API,
            );
          },
          navigate,
        }),
      );

      if (location.state?.edit) {
        dispatch(EditorActions.readOnly(false));
        navigate(location, {
          replace: true,
          state: { edit: false, editTopic: false },
        });
      }

      loadedPostIdRef.current = post.id;
      loadedIsPostEditableRef.current = isPostEditable;
    }

    return undefined;
  }, [dispatch, error, isPostEditable, loading, navigate, post, location]);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const grantAccessTo = searchParams.get(GRANT_ACCESS_TO);
    const user = grantAccessTo ? userMap[grantAccessTo] : null;

    if (user && post) {
      setSearchParams({}, { replace: true });

      if (postPolicyCheck(PostPolicyAction.ViewPost, post, user)) {
        return;
      }

      addUser(post, user.id);

      dispatch(
        UIActions.pushToast(
          postToasts.postShared({
            onViewSharing: () => {
              setIsShareDropdownOpen({ open: true, autoFocus: false });
            },
          }),
        ),
      );
    }
  }, [
    addUser,
    dispatch,
    post,
    postPolicyCheck,
    navigate,
    setIsShareDropdownOpen,
    userMap,
    searchParams,
    setSearchParams,
  ]);

  const anchorScrollingHandlerProps = useAnchorScrollingHandler(quill, postId, {
    updateLocationHash: true,
  });
  const handleTextChange: TextChangeHandler = useCallback(
    (_delta, _oldContent, source) => {
      if (source === Quill.sources.API) {
        receivedRef.current = new Delta();
      }
    },
    [],
  );

  const handlePerformanceMeasure: PerformanceMeasureHandler = useCallback(
    (durations) => {
      track(ACTION_EDITOR_RENDER_DURATION, {
        category: CATEGORY_PRODUCT,
        label: postId,
        ...durations,
      });
    },
    [postId],
  );

  const currentBannerUrl = post?.banner?.original ?? null;
  const currentPostBanner = useAppSelector(
    (state) => state.editor.currentPostBanner,
  );

  const banner =
    currentPostBanner ??
    (currentBannerUrl ? { image: currentBannerUrl } : null);

  const postLayoutMagneticWallRef = useRef<HTMLElement | null>(null);

  const isEditingMeta =
    !readOnly ||
    (post != null &&
      !isLocked &&
      postPolicyCheck(PostPolicyAction.EditPost, post, currentUser) &&
      post.externalPost != null);

  const hasMagneticWallItems = useHasMagneticWallItems(post);
  const hasHeadings = useHasHeadings();
  const shouldShowEditorPlaceholder = useShouldShowEditorPlaceholder();

  return (
    <Page
      topbar={
        <InternalTopbar postActions={post && <PostActions post={post} />} />
      }
    >
      <PostLayoutProvider
        expandableAreas={{
          sidebar: true,
          magneticWall: hasMagneticWallItems,
          toc: hasHeadings,
        }}
      >
        <PendingMagneticWallItemProvider>
          <PostViewContainer {...anchorScrollingHandlerProps}>
            <PageMeta title={`${title || 'Post'} - Slab`} />
            <PostLayout.Root
              ref={postLayoutRootRef}
              hasBanner={banner != null}
              topbar={post && <PostStateBar post={post} />}
              forceExpandOnMonitor
            >
              {isVersionLoaded && post != null ? (
                <WiderLineSizeContext
                  containerRef={postLayoutRootRef}
                  editor={quill?.scroll.domNode}
                >
                  <UIElementResizeContext>
                    <PostLayout.Above>
                      <PostTopicBar
                        post={post}
                        editing={isEditingMeta}
                        series={post.series}
                      />
                    </PostLayout.Above>
                    {banner && (
                      <PostViewBannerEditor
                        setInnerBannerRef={innerBannerRef}
                        banner={banner}
                        currentBannerUrl={currentBannerUrl}
                        post={post}
                      />
                    )}
                    <PostLayout.ControlBar>
                      <ControlBar
                        post={post}
                        editing={isEditingMeta}
                        disableInsightsButton={shouldShowEditorPlaceholder}
                      />
                    </PostLayout.ControlBar>
                    <PostLayout.TOC>
                      <PostStickyControl
                        postId={postId}
                        hasBanner={banner != null}
                      />
                    </PostLayout.TOC>
                    <PostLayout.Editor hasBanner={banner != null}>
                      <PostActivityTracker
                        postId={postId}
                        queryId={queryId}
                        resultId={resultId}
                      />
                      <PostSaveTracker postId={postId} />
                      <HoverContentPreview>
                        <MentionPickerProvider post={post}>
                          {data?.entityAssetToken && (
                            <AssetTokenProvider
                              entityAssetToken={data.entityAssetToken}
                            >
                              <Editor
                                post={post}
                                highlightedRangeIndex={highlightedRangeIndex}
                                onTextChange={handleTextChange}
                                onPerformanceMeasure={handlePerformanceMeasure}
                                showPlaceholder={shouldShowEditorPlaceholder}
                              />
                            </AssetTokenProvider>
                          )}
                        </MentionPickerProvider>
                      </HoverContentPreview>
                    </PostLayout.Editor>
                    <PostLayout.MagneticWall ref={postLayoutMagneticWallRef}>
                      {quill && (
                        <MentionPickerProvider post={post}>
                          <MagneticWall
                            quill={quill}
                            post={post}
                            canEditPost={isPostEditable}
                            innerBannerRef={innerBannerRef}
                            hasBanner={banner != null}
                          />
                        </MentionPickerProvider>
                      )}
                    </PostLayout.MagneticWall>
                    <PostLayout.Below>
                      <FooterControlBar
                        postId={post.id}
                        postDetails={post}
                        series={post.series}
                      />
                      {post.series && (
                        <PostSeriesFooter
                          postId={post.id}
                          series={post.series}
                        />
                      )}
                    </PostLayout.Below>
                  </UIElementResizeContext>
                </WiderLineSizeContext>
              ) : (
                <>
                  <PostLayout.Above />
                  <PostLayout.Editor>
                    <PostSkeleton />
                  </PostLayout.Editor>
                </>
              )}
            </PostLayout.Root>
          </PostViewContainer>
        </PendingMagneticWallItemProvider>
      </PostLayoutProvider>
    </Page>
  );
};

export const PostViewContainer = ({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={classNames(styles.container, className)} {...props}>
      {children}
    </div>
  );
};

const PostViewWrapper = () => {
  const [postId] = useSlug('postSlug');
  const [searchParams] = useSearchParams();
  const canDisplayPostHistoryView = useCanDisplayPostHistoryView();

  if (searchParams.has('histories')) {
    return canDisplayPostHistoryView ? (
      <PostHistoryView postId={postId} />
    ) : (
      <Navigate to={postPath({ id: postId, title: '' })} replace />
    );
  }

  const comparePostId = searchParams.get('compare');
  if (comparePostId) {
    return (
      <ComparePostsView basePostId={postId} comparePostId={comparePostId} />
    );
  }

  return (
    <QuillContext>
      <PostView key={postId} postId={postId} />
    </QuillContext>
  );
};

export default PostViewWrapper;
